import {useCallback, useEffect, useReducer} from "react";
import {
    getActiveRestoreJob,
    getRestoreJob,
    restoreBackup,
} from "@/api/BackupApi.ts";
import {RestoreProgressEvent} from "@/backup/RestoreProgress.ts";
import {useBackupRestoreRealtime} from "@/backup/useBackupRealtime.ts";

interface RestoreJobState {
    event: RestoreProgressEvent | null;
    starting: boolean;
    error: string | null;
    completionHandled: boolean;
}

type RestoreJobAction =
    | {type: "START"}
    | {type: "START_FAILED"; error: string}
    | {type: "STATUS_FAILED"; error: string}
    | {type: "EVENT"; event: RestoreProgressEvent}
    | {type: "COMPLETION_HANDLED"};

const initialState: RestoreJobState = {
    event: null,
    starting: false,
    error: null,
    completionHandled: false,
};

function reducer(state: RestoreJobState, action: RestoreJobAction): RestoreJobState {
    switch (action.type) {
        case "START":
            return {...state, event: null, starting: true, error: null, completionHandled: false};
        case "START_FAILED":
            return {...state, starting: false, error: action.error};
        case "STATUS_FAILED":
            return state.error === action.error ? state : {...state, error: action.error};
        case "COMPLETION_HANDLED":
            return {...state, completionHandled: true};
        case "EVENT": {
            if (state.event?.jobId === action.event.jobId && action.event.revision <= state.event.revision) {
                return state.error && !action.event.failed ? {...state, error: null} : state;
            }
            return {
                event: action.event,
                starting: false,
                error: action.event.failed
                    ? action.event.error ?? "Die Wiederherstellung ist fehlgeschlagen."
                    : null,
                completionHandled: false,
            };
        }
    }
}

export function useRestoreJob(onFinished: (event: RestoreProgressEvent) => void) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const event = state.event;
    const jobId = event?.jobId;
    const jobFinished = event?.finished;
    const handleProgress = useCallback(
        (progress: RestoreProgressEvent) => dispatch({type: "EVENT", event: progress}),
        [],
    );
    useBackupRestoreRealtime(jobFinished ? undefined : jobId, handleProgress);

    useEffect(() => {
        let cancelled = false;
        getActiveRestoreJob()
            .then(job => {
                if (!cancelled && job) dispatch({type: "EVENT", event: job});
            })
            .catch(() => {
                if (!cancelled) dispatch({type: "STATUS_FAILED", error: "Der aktive Restore-Status konnte nicht geladen werden."});
            });
        return () => {
            cancelled = true;
        };
    }, []);


    useEffect(() => {
        if (!jobId || jobFinished) return;
        const interval = window.setInterval(() => {
            void getRestoreJob(jobId)
                .then(progress => dispatch({type: "EVENT", event: progress}))
                .catch(() => dispatch({type: "STATUS_FAILED",
                    error: "Der Restore-Status konnte nicht aktualisiert werden. Ein neuer Versuch läuft."}));
        }, 2000);
        return () => window.clearInterval(interval);
    }, [jobFinished, jobId]);

    useEffect(() => {
        if (!event?.finished || state.completionHandled) return;
        dispatch({type: "COMPLETION_HANDLED"});
        onFinished(event);
    }, [event, onFinished, state.completionHandled]);

    const start = useCallback(async (backupId: string) => {
        if (state.starting || (state.event && !state.event.finished)) return;
        dispatch({type: "START"});
        try {
            dispatch({type: "EVENT", event: await restoreBackup(backupId)});
        } catch {
            dispatch({type: "START_FAILED", error: "Die Wiederherstellung konnte nicht gestartet werden."});
        }
    }, [state.event, state.starting]);

    return {
        event,
        error: state.error,
        busy: state.starting || Boolean(event && !event.finished),
        start,
    };
}
